import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { InterviewForm, SimulatorForm, today } from "@/components/AssessmentForms";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

const SIM_DEFAULT = { date: today(), assessor: "", simulator_location: "", coordination: 3, spatial_awareness: 3, control_accuracy: 3, instruction_following: 3, concentration: 3, learning_rate: 3, response_to_correction: 3, safety_behaviour: 3, confidence_without_rushing: 3, comments: "", recommendation: "suitable" };
const INT_DEFAULT = { date: today(), assessor: "", scores: {}, red_flags: [], comments: "" };

export default function AssessCandidate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialTab = ["simulator", "interview"].includes(params.get("tab")) ? params.get("tab") : "interview";
  const [candidate, setCandidate] = useState(null);
  const [sim, setSim] = useState(SIM_DEFAULT);
  const [interview, setInterview] = useState(INT_DEFAULT);
  const [saving, setSaving] = useState("");
  const [reportFile, setReportFile] = useState(null);

  useEffect(() => {
    api.get(`/staff/candidates/${id}`).then((r) => {
      const d = r.data;
      setCandidate(d.summary);
      if (d.simulator) setSim({ ...SIM_DEFAULT, ...d.simulator });
      if (d.interview) setInterview({ ...INT_DEFAULT, ...d.interview, red_flags: d.interview.red_flags || d.interview.concerns || [] });
    }).catch(() => toast.error("Could not load candidate"));
  }, [id]);

  if (!candidate) return <div className="h-8 w-8 border-4 border-steel border-t-lomond rounded-full animate-spin" data-testid="assess-loading" />;

  const save = async (kind, payload, key) => {
    setSaving(key);
    try {
      await api.post(`/staff/candidates/${id}/${kind}`, payload);
      toast.success("Assessment saved.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const uploadReport = async () => {
    if (!reportFile) return;
    const fd = new FormData();
    fd.append("file", reportFile);
    try {
      await api.post(`/staff/candidates/${id}/simulator-report`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Simulator report uploaded.");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="assess-candidate-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={() => navigate(`/staff/candidates/${id}`)} className="border-steel text-charcoal bg-white" data-testid="back-to-profile">
          <ArrowLeft className="h-4 w-4 mr-1" /> Profile
        </Button>
        <div>
          <h1 className="font-heading font-extrabold uppercase tracking-tight text-charcoal text-xl sm:text-2xl" data-testid="assess-heading">
            Assessment Day — {candidate.name}
          </h1>
          <p className="text-xs text-slate-500 font-mono uppercase tracking-widest">Interview + simulator — live-machine practical happens in the Training Academy</p>
        </div>
      </div>

      <Tabs defaultValue={initialTab} data-testid="assess-tabs">
        <TabsList className="bg-white border border-steel w-full sm:w-auto grid grid-cols-2">
          <TabsTrigger value="interview" data-testid="tab-interview">Interview</TabsTrigger>
          <TabsTrigger value="simulator" data-testid="tab-simulator">Simulator</TabsTrigger>
        </TabsList>

        <TabsContent value="interview">
          <section className="bg-white border border-steel rounded-md p-6 mt-4">
            <InterviewForm value={interview} onChange={setInterview} onSave={() => save("interview", interview, "interview")} saving={saving === "interview"} />
          </section>
        </TabsContent>

        <TabsContent value="simulator">
          <section className="bg-white border border-steel rounded-md p-6 mt-4">
            <SimulatorForm value={sim} onChange={setSim} onSave={() => save("simulator", sim, "sim")} saving={saving === "sim"}
              onUploadReport={uploadReport} reportFile={reportFile} setReportFile={setReportFile} />
            {sim.report_url && <a href={fileUrl(sim.report_url)} target="_blank" rel="noreferrer" className="inline-block mt-2 text-xs text-lomond underline" data-testid="sim-report-link">View uploaded simulator report</a>}
          </section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
